'use client'

import { useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'
import { motion } from 'framer-motion'
import { Check, ChevronRight, Coins, Gift, Medal, ShoppingBag, Star, Ticket, TrendingUp } from 'lucide-react'
import { Button } from '@/components/elements/Button'
import Progress from '@/components/elements/Progress'
import Skeleton from '@/components/elements/Skeleton'
import { useAuth } from '@/lib/commerce/hooks'
import { getLoyaltyTiersApi, getLoyaltySummaryApi } from '@/lib/commerce/services/loyalty-api'
import { navigateUrl } from '@/utils/client-request'
import { classNames, formatImageURL, formatNumber, formatPrice, getVendorPrimaryColor, isCustomerAuthActive, resolveColorHex } from '@/utils/util'
import { STATUS_LABELS } from '@/utils/order'
import ConvertVoucherModal from './ConvertVoucherModal'

export default function Loyalty({ vendor }: { vendor: any }) {
  const pathname = usePathname()
  const { session, isLoggedIn } = useAuth()
  const primary = resolveColorHex(getVendorPrimaryColor(vendor))
  const primaryTint = `color-mix(in oklch, ${primary} 10%, white)`

  const [program, setProgram] = useState<any>(null)
  const [loadingProgram, setLoadingProgram] = useState(true)
  const [summary, setSummary] = useState<any>(null)
  const [loadingSummary, setLoadingSummary] = useState(false)
  const [convertOpen, setConvertOpen] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)

  const tiers = program?.tiers || []
  const ctx = { vendorId: vendor?.id, session: session || undefined }

  useEffect(() => {
    let cancelled = false
    getLoyaltyTiersApi({ vendorId: vendor?.id }).then((programRes: any) => {
      if (cancelled) return
      setProgram(programRes?.data || null)
    }).catch(() => { }).finally(() => { if (!cancelled) setLoadingProgram(false) })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!isLoggedIn) return
    let cancelled = false
    setLoadingSummary(true)
    getLoyaltySummaryApi(ctx).then((summaryRes) => {
      if (cancelled) return
      setSummary(summaryRes?.data || null)
    }).catch(() => { }).finally(() => { if (!cancelled) setLoadingSummary(false) })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, refreshKey])

  const benefitLabel = (benefit: any) => {
    if (benefit.type === 'percentDiscount') return `${benefit.valuePercent}% хөнгөлөлт`
    if (benefit.type === 'fixedDiscount') return `${formatPrice(benefit.valueAmount)} хөнгөлөлт`
    if (benefit.type === 'freeShipping') return 'Үнэгүй хүргэлт'
    if (benefit.type === 'shippingDiscount') return `Хүргэлт ${benefit.valuePercent}% хөнгөлөлттэй`
    return 'Нэмэлт урамшуулал'
  }

  const progressPercent = summary?.nextTier
    ? Math.min(100, Math.round((summary.qualifyingSpend / summary.nextTier.minAnnualSpend) * 100))
    : 100

  const steps = [
    { icon: ShoppingBag, t: 'Худалдан авалт бүрээс оноо цуглуул', s: `Захиалга "${STATUS_LABELS[program?.awardStatus] || STATUS_LABELS.paid}" төлөвт орох бүрд түвшнийхээ хувиар оноо орно.` },
    { icon: TrendingUp, t: 'Түвшин ахиж, илүү урамшуулал ав', s: 'Жилийн худалдан авалтаараа дээд түвшинд хүрч, илүү хөнгөлөлт эдлээрэй.' },
    { icon: Ticket, t: 'Оноогоо ваучер болгон ашигла', s: 'Цуглуулсан оноогоо ваучер болгон дараагийн захиалгадаа хэрэглэнэ.' },
  ]

  return (
    <div className="bg-white">
      <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-10 pt-0 md:pt-8 pb-12 md:pb-20">
        <div className="hidden md:flex items-center gap-1.5 text-xs text-muted-foreground">
          <a href={navigateUrl('/', pathname)} className="hover:text-foreground">Нүүр</a>
          <ChevronRight className="h-3 w-3 opacity-50" />
          <span className="text-foreground">Лоялти хөтөлбөр</span>
        </div>

        <div className="mx-auto max-w-190">
          <div>
            <div className="mt-8 flex items-center gap-5 sm:gap-6">
              <div className="h-20 w-20 sm:h-24 sm:w-24 shrink-0 rounded-full grid place-items-center text-white" style={{ background: primary }}>
                <Medal className="h-10 w-10 sm:h-12 sm:w-12" strokeWidth={2} />
              </div>
              <div className="min-w-0">
                <h1 className="font-gilroy text-[28px] sm:text-[44px] font-bold tracking-tight text-foreground leading-[1.05]">
                  Лоялти хөтөлбөр
                </h1>
              </div>
            </div>

            <p className="mt-5 text-[15px] leading-relaxed text-foreground/65 max-w-2xl">
              Худалдан авалт бүрээсээ оноо цуглуулж, түвшин ахих тусам илүү их хөнгөлөлт аваарай.
              Цуглуулсан оноогоо ваучер болгон дараагийн захиалгадаа ашиглах боломжтой.
            </p>
          </div>

          {!isLoggedIn && isCustomerAuthActive(vendor) && loadingProgram && (
            <Skeleton className="mt-8 h-36 w-full rounded-2xl" />
          )}

          {!isLoggedIn && isCustomerAuthActive(vendor) && !loadingProgram && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mt-8 rounded-2xl p-6 sm:p-7" style={{ background: primaryTint }}>
              <div className="flex flex-col sm:flex-row sm:items-center gap-5">
                <div className="h-14 w-14 shrink-0 rounded-full grid place-items-center text-white" style={{ background: primary }}>
                  <Gift className="h-7 w-7" strokeWidth={2} />
                </div>
                <div className="flex-1 min-w-0">
                  {program?.welcomeBonusPoints > 0 ? (
                    <>
                      <div className="font-gilroy text-2xl sm:text-3xl font-bold tracking-tight" style={{ color: primary }}>+{formatNumber(program.welcomeBonusPoints)} оноо</div>
                      <p className="mt-1 text-sm text-foreground/70">Бүртгэлээ үүсгэж үнэгүй оноо аваад, ваучер болгон ашиглаарай.</p>
                    </>
                  ) : (
                    <>
                      <div className="font-gilroy text-xl sm:text-2xl font-bold tracking-tight text-foreground">Оноо цуглуулж эхлээрэй</div>
                      <p className="mt-1 text-sm text-foreground/70">Бүртгүүлсэн худалдан авагч бүр худалдан авалтаасаа оноо цуглуулна.</p>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2.5 shrink-0">
                  <Button tag="a" href={navigateUrl('/register', pathname)} className="text-white!" style={{ background: primary }}>Бүртгүүлэх</Button>
                  <Button tag="a" href={navigateUrl(`/login?redirect=/loyalty`, pathname)} variant="outline">Нэвтрэх</Button>
                </div>
              </div>
            </motion.div>
          )}

          {isLoggedIn && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mt-8">
              {loadingSummary && !summary ? (
                <Skeleton className="h-40 w-full rounded-2xl" />
              ) : summary ? (
                <div className="border border-border rounded-2xl p-5 sm:p-6">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      {summary.currentTier?.icon ? (
                        <img src={formatImageURL(summary.currentTier.icon)} alt={summary.currentTier.name} className="size-12 rounded-full object-cover border border-border" />
                      ) : (
                        <div className="size-12 rounded-full bg-muted flex items-center justify-center"><Medal className="size-6 opacity-60" /></div>
                      )}
                      <div>
                        <div className="text-sm text-muted-foreground">Таны түвшин</div>
                        <div className="text-lg font-semibold">{summary.currentTier?.name || '—'}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-muted-foreground">Онооны үлдэгдэл</div>
                      <div className="text-2xl font-bold tabular-nums">{formatNumber(summary.balance || 0)}</div>
                    </div>
                    <Button icon={Ticket} className="text-white!" style={{ background: primary }} onClick={() => setConvertOpen(true)} isActive={(summary.balance || 0) >= (program?.minPointsToConvert || 1)}>Ваучер болгох</Button>
                  </div>
                  <div className="mt-5">
                    {summary.nextTier ? (
                      <>
                        <div className="flex items-center justify-between text-sm mb-1.5">
                          <span className="text-muted-foreground">Энэ жилийн худалдан авалт: <span className="font-medium text-foreground">{formatPrice(summary.qualifyingSpend || 0)}</span></span>
                          <span className="text-muted-foreground">{summary.nextTier.name}: {formatPrice(summary.nextTier.minAnnualSpend)}</span>
                        </div>
                        <Progress value={progressPercent} />
                        <p className="text-xs text-muted-foreground mt-1.5">{summary.nextTier.name} түвшинд хүрэхэд {formatPrice(summary.nextTier.remainingSpend)} дутуу байна</p>
                      </>
                    ) : (
                      <p className="text-sm text-muted-foreground flex items-center gap-1.5"><Star className="size-4" style={{ color: primary }} /> Та хамгийн дээд түвшинд байна. Энэ жилийн худалдан авалт {formatPrice(summary.qualifyingSpend || 0)}</p>
                    )}
                  </div>
                  <a href={navigateUrl('/points', pathname)} className="mt-4 text-sm font-medium inline-flex items-center gap-0.5 hover:opacity-80" style={{ color: primary }}>
                    Онооны түүх үзэх
                    <ChevronRight className="size-4" />
                  </a>
                </div>
              ) : null}
            </motion.div>
          )}

          <div className="mt-10 border-t border-border pt-8">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Хэрхэн ажилладаг вэ</div>
            <ul className="mt-5 space-y-5">
              {steps.map((step, index) => {
                const Icon = step.icon
                return (
                  <li key={index} className="flex gap-4">
                    <div className="mt-0.5 h-8 w-8 shrink-0 grid place-items-center rounded-full" style={{ background: primaryTint, color: primary }}>
                      <Icon className="h-4 w-4" strokeWidth={2} />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[15px] font-semibold text-foreground">{step.t}</div>
                      <div className="mt-0.5 text-[13.5px] leading-relaxed text-muted-foreground">{step.s}</div>
                    </div>
                  </li>
                )
              })}
            </ul>
          </div>
        </div>

        <div className="mx-auto max-w-5xl mt-10 border-t border-border pt-8">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Түвшингүүд</div>
          {loadingProgram && (
            <div className="mt-5 grid gap-5 md:grid-cols-3">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="border border-border rounded-2xl p-5">
                  <div className="flex items-center gap-3">
                    <Skeleton className="size-12 shrink-0 rounded-full" />
                    <div className="flex-1 min-w-0 space-y-2">
                      <Skeleton className="h-5 w-24" />
                      <Skeleton className="h-3 w-32" />
                    </div>
                  </div>
                  <div className="mt-4 space-y-2.5">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-5/6" />
                    <Skeleton className="h-4 w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          )}
          {!loadingProgram && (
          <div className={classNames('mt-5 grid gap-5', tiers.length >= 3 ? 'md:grid-cols-3' : tiers.length === 2 ? 'md:grid-cols-2' : 'max-w-lg')}>
            {tiers.map((tier: any, index: number) => {
              const isCurrent = summary?.currentTier?.id === tier.id
              return (
                <motion.div key={tier.id} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} viewport={{ once: true }} className={classNames('border rounded-2xl p-5 flex flex-col', isCurrent ? 'border-2' : 'border-border')} style={isCurrent ? { borderColor: primary } : undefined}>
                  <div className="flex items-center gap-3">
                    {tier.icon ? (
                      <img src={formatImageURL(tier.icon)} alt={tier.name} className="size-12 rounded-full object-cover border border-border" />
                    ) : (
                      <div className="size-12 rounded-full bg-muted flex items-center justify-center"><Medal className="size-6 opacity-60" /></div>
                    )}
                    <div>
                      <div className="font-semibold text-lg flex items-center gap-2">
                        {tier.name}
                        {isCurrent && <span className="text-xs font-medium px-2 py-0.5 rounded-full text-white" style={{ background: primary }}>Таны түвшин</span>}
                      </div>
                      <div className="text-sm text-muted-foreground">{tier.minAnnualSpend > 0 ? `${formatPrice(tier.minAnnualSpend)}-с дээш` : 'Эхлэх түвшин'}</div>
                    </div>
                  </div>
                  <ul className="mt-4 space-y-2 text-sm flex-1">
                    <li className="flex items-start gap-2">
                      <Coins className="size-4 mt-0.5 shrink-0" style={{ color: primary }} />
                      <span>{formatNumber(tier.spendPerPoint)}₮ тутамд {formatNumber(tier.pointsPerUnit)} оноо</span>
                    </li>
                    {tier.reviewRewardPoints > 0 && (
                      <li className="flex items-start gap-2">
                        <Star className="size-4 mt-0.5 shrink-0" style={{ color: primary }} />
                        <span>Сэтгэгдэл бүрд {formatNumber(tier.reviewRewardPoints)} оноо</span>
                      </li>
                    )}
                    {(tier.benefits || []).map((benefit: any) => (
                      <li key={benefit.id} className="flex items-start gap-2">
                        <Check className="size-4 mt-0.5 shrink-0" style={{ color: primary }} />
                        <span>{benefitLabel(benefit)}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              )
            })}
          </div>
          )}
          <p className="text-xs text-muted-foreground mt-4 text-center">Түвшин жилийн худалдан авалтаар тодорхойлогдож, жил бүр шинээр эхэлнэ. Цуглуулсан оноо хадгалагдана.</p>
        </div>

      </div>

      <ConvertVoucherModal open={convertOpen} setOpen={setConvertOpen} vendor={vendor} program={program} balance={summary?.balance || 0} onConverted={() => setRefreshKey((key) => key + 1)} />
    </div>
  )
}
